import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import Campaign from '@mui/icons-material/Campaign'
import Phone from '@mui/icons-material/Phone'
import Email from '@mui/icons-material/Email'
import Notifications from '@mui/icons-material/Notifications'
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline'
import OpenInNew from '@mui/icons-material/OpenInNew'
import ContentCopy from '@mui/icons-material/ContentCopy'
import Payments from '@mui/icons-material/Payments'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import History from '@mui/icons-material/History'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  getMessageMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  allotSupportClinicBrandCommsQuota,
  createSupportClinicBrandCommsQuotaPurchaseRequest,
  getSupportClinicBrandCommsQuota,
  getSupportClinicBrandCommsQuotaRequests,
} from 'src/supportPortal/services/clinicBrandCommsQuotaService'

const QUOTA_FIELDS = [
  {
    key: 'waCredits',
    label: 'WhatsApp',
    icon: Campaign,
    color: '#0f9d58',
  },
  {
    key: 'smsCredits',
    label: 'SMS',
    icon: ChatBubbleOutline,
    color: '#7c3aed',
  },
  {
    key: 'emailCredits',
    label: 'Email',
    icon: Email,
    color: '#ef6c00',
  },
  {
    key: 'pushCredits',
    label: 'Push',
    icon: Notifications,
    color: '#1565c0',
  },
  {
    key: 'outcallCredits',
    label: 'Outcall',
    icon: Phone,
    color: '#b91c1c',
  },
]

const EMPTY_CREDIT_FORM = {
  waCredits: '0',
  smsCredits: '0',
  emailCredits: '0',
  pushCredits: '0',
  outcallCredits: '0',
}

const EMPTY_PURCHASE_FORM = {
  ...EMPTY_CREDIT_FORM,
  requestedAmount: '',
  currencyCode: 'INR',
  reference: '',
  note: '',
}

const EMPTY_ALLOT_FORM = {
  ...EMPTY_CREDIT_FORM,
  reference: '',
  note: '',
}

function normalizeCountInput(value) {
  if (value == null || value === '') {
    return '0'
  }
  return String(value).replace(/[^\d]/g, '') || '0'
}

function formatDateTime(value) {
  if (!value) {
    return 'Not available'
  }
  const parsedDate = new Date(value)
  if (Number.isNaN(parsedDate.getTime())) {
    return value
  }
  return parsedDate.toLocaleString()
}

function formatAmount(value, currencyCode = 'INR') {
  const numericValue = Number(value || 0)
  if (!Number.isFinite(numericValue)) {
    return null
  }
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currencyCode || 'INR',
      maximumFractionDigits: 2,
    }).format(numericValue)
  } catch (e) {
    return `${currencyCode || 'INR'} ${numericValue}`
  }
}

function getStatusChipColor(status) {
  switch (status) {
    case 'completed':
      return 'success'
    case 'payment_pending':
      return 'warning'
    case 'payment_failed':
      return 'error'
    case 'fulfilling':
      return 'info'
    default:
      return 'default'
  }
}

function buildCreditPayload(formState, clinicBrandId) {
  return {
    clinicBrandId,
    waCredits: Number(formState?.waCredits || 0),
    smsCredits: Number(formState?.smsCredits || 0),
    emailCredits: Number(formState?.emailCredits || 0),
    pushCredits: Number(formState?.pushCredits || 0),
    outcallCredits: Number(formState?.outcallCredits || 0),
  }
}

function totalCreditsFromForm(formState = {}) {
  return QUOTA_FIELDS.reduce((acc, field) => acc + Number(formState?.[field.key] || 0), 0)
}

function totalCreditsFromQuota(quota = {}) {
  return QUOTA_FIELDS.reduce((acc, field) => acc + Number(quota?.[field.key] || 0), 0)
}

function renderCreditChips(source) {
  return QUOTA_FIELDS.filter((field) => Number(source?.[field.key] || 0) > 0).map((field) => {
    const Icon = field.icon
    return (
      <Chip
        key={field.key}
        size="small"
        icon={<Icon sx={{ color: `${field.color} !important` }} />}
        label={`${field.label} ${Number(source?.[field.key] || 0)}`}
        sx={{
          color: field.color,
          backgroundColor: alpha(field.color, 0.08),
          border: `1px solid ${alpha(field.color, 0.18)}`,
          fontWeight: 600,
        }}
      />
    )
  })
}

export default function ClinicBrandCommsQuotaDialog({
  open,
  onClose,
  clinicBrand,
}) {
  const clinicBrandId = clinicBrand?.id || ''
  const clinicBrandTitle = clinicBrand?.title || clinicBrand?.brandName || 'Clinic brand'
  const [loading, setLoading] = useState(false)
  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false)
  const [allotSubmitting, setAllotSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [quota, setQuota] = useState(null)
  const [requests, setRequests] = useState([])
  const [purchaseForm, setPurchaseForm] = useState(EMPTY_PURCHASE_FORM)
  const [allotForm, setAllotForm] = useState(EMPTY_ALLOT_FORM)

  const totalQuotaCredits = useMemo(() => totalCreditsFromQuota(quota), [quota])
  const purchaseCredits = useMemo(() => totalCreditsFromForm(purchaseForm), [purchaseForm])
  const allotCredits = useMemo(() => totalCreditsFromForm(allotForm), [allotForm])

  useEffect(() => {
    if (!open || !clinicBrandId) {
      return
    }

    let active = true

    async function loadDialogData() {
      setLoading(true)
      setError('')
      try {
        const [quotaResp, requestsResp] = await Promise.all([
          getSupportClinicBrandCommsQuota(clinicBrandId),
          getSupportClinicBrandCommsQuotaRequests(clinicBrandId, 20),
        ])
        if (!active) {
          return
        }
        if (!isCommonResponseSuccessfulV2(quotaResp)) {
          throw new Error(getMessageMapFromCommonResponse(quotaResp) || 'Unable to load communication quota')
        }
        const nextQuota = getDataMapFromCommonResponse(quotaResp)?.['clinic-brand-comms-quota'] || null
        setQuota(nextQuota)

        if (isCommonResponseSuccessfulV2(requestsResp)) {
          const nextRequests = getDataMapFromCommonResponse(requestsResp)?.['clinic-brand-comms-quota-request'] || []
          setRequests(Array.isArray(nextRequests) ? nextRequests : [])
        } else {
          setRequests([])
        }
      } catch (loadError) {
        if (!active) {
          return
        }
        setError(loadError?.message || 'Unable to load communication quota controls')
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    loadDialogData()

    return () => {
      active = false
    }
  }, [clinicBrandId, open])

  const handleClose = () => {
    if (purchaseSubmitting || allotSubmitting) {
      return
    }
    setError('')
    onClose?.()
  }

  const updatePurchaseField = (key) => (event) => {
    const rawValue = event?.target?.value ?? ''
    setPurchaseForm((prev) => ({
      ...prev,
      [key]: QUOTA_FIELDS.some((field) => field.key === key) ? normalizeCountInput(rawValue) : rawValue,
    }))
  }

  const updateAllotField = (key) => (event) => {
    const rawValue = event?.target?.value ?? ''
    setAllotForm((prev) => ({
      ...prev,
      [key]: QUOTA_FIELDS.some((field) => field.key === key) ? normalizeCountInput(rawValue) : rawValue,
    }))
  }

  async function reloadAllData() {
    const [quotaResp, requestsResp] = await Promise.all([
      getSupportClinicBrandCommsQuota(clinicBrandId),
      getSupportClinicBrandCommsQuotaRequests(clinicBrandId, 20),
    ])
    if (isCommonResponseSuccessfulV2(quotaResp)) {
      setQuota(getDataMapFromCommonResponse(quotaResp)?.['clinic-brand-comms-quota'] || null)
    }
    if (isCommonResponseSuccessfulV2(requestsResp)) {
      const nextRequests = getDataMapFromCommonResponse(requestsResp)?.['clinic-brand-comms-quota-request'] || []
      setRequests(Array.isArray(nextRequests) ? nextRequests : [])
    }
  }

  const handleCreatePurchaseRequest = async () => {
    if (!clinicBrandId) {
      return
    }
    if (purchaseCredits <= 0) {
      toast.error('Add at least one communication quota item before creating a payment link')
      return
    }
    const amountValue = Number(purchaseForm?.requestedAmount || 0)
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error('Enter a payment amount before creating the payment link')
      return
    }

    setPurchaseSubmitting(true)
    setError('')
    try {
      const response = await createSupportClinicBrandCommsQuotaPurchaseRequest({
        ...buildCreditPayload(purchaseForm, clinicBrandId),
        requestedAmount: amountValue,
        currencyCode: purchaseForm?.currencyCode?.trim() || 'INR',
        paymentGateway: 'paymentLink',
        paymentMethod: 'payment-link',
        reference: purchaseForm?.reference?.trim() || '',
        note: purchaseForm?.note?.trim() || '',
      })
      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error(getMessageMapFromCommonResponse(response) || 'Unable to create payment link')
      }
      const createdRequest = getDataMapFromCommonResponse(response)?.['clinic-brand-comms-quota-request'] || null
      toast.success(`Payment link created for ${clinicBrandTitle}`)
      if (createdRequest?.paymentLinkUrl) {
        window.open(createdRequest.paymentLinkUrl, '_blank', 'noopener,noreferrer')
      }
      setPurchaseForm(EMPTY_PURCHASE_FORM)
      await reloadAllData()
    } catch (requestError) {
      const message = requestError?.message || 'Unable to create payment link'
      setError(message)
      toast.error(message)
    } finally {
      setPurchaseSubmitting(false)
    }
  }

  const handleAllotQuota = async () => {
    if (!clinicBrandId) {
      return
    }
    if (allotCredits <= 0) {
      toast.error('Add at least one communication quota item before allotting quota')
      return
    }

    setAllotSubmitting(true)
    setError('')
    try {
      const response = await allotSupportClinicBrandCommsQuota({
        ...buildCreditPayload(allotForm, clinicBrandId),
        reference: allotForm?.reference?.trim() || '',
        note: allotForm?.note?.trim() || '',
      })
      if (!isCommonResponseSuccessfulV2(response)) {
        throw new Error(getMessageMapFromCommonResponse(response) || 'Unable to allot communication quota')
      }
      toast.success(`Quota allotted to ${clinicBrandTitle}`)
      setAllotForm(EMPTY_ALLOT_FORM)
      await reloadAllData()
    } catch (requestError) {
      const message = requestError?.message || 'Unable to allot communication quota'
      setError(message)
      toast.error(message)
    } finally {
      setAllotSubmitting(false)
    }
  }

  const handleCopyPaymentLink = async (paymentLinkUrl) => {
    if (!paymentLinkUrl) {
      toast.error('No payment link is available for this request')
      return
    }
    try {
      await navigator.clipboard.writeText(paymentLinkUrl)
      toast.success('Payment link copied')
    } catch (copyError) {
      toast.error('Unable to copy payment link')
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack spacing={0.75}>
          <Typography variant="h6" fontWeight={800}>
            Communication Quota
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {clinicBrandTitle}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Quota is no longer edited as a raw snapshot here. Purchase adds credits through a payment link, and allotment records an explicit support-side adjustment with history.
          </Alert>

          {error ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.25,
                  borderRadius: 2.5,
                  background: 'linear-gradient(180deg, rgba(248,250,252,0.95) 0%, rgba(255,255,255,0.98) 100%)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.25}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                >
                  <Stack spacing={0.35}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      Current quota balance
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last updated {formatDateTime(quota?.modifiedTime)}
                      {quota?.modifiedBy ? ` by ${quota.modifiedBy}` : ''}
                    </Typography>
                  </Stack>
                  <Chip
                    label={`Total credits ${totalQuotaCredits}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>

                <Grid container spacing={1.5} sx={{ mt: 0.75 }}>
                  {QUOTA_FIELDS.map((field) => {
                    const Icon = field.icon
                    return (
                      <Grid item xs={12} sm={6} md={4} key={field.key}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderColor: alpha(field.color, 0.2),
                            height: '100%',
                          }}
                        >
                          <Stack direction="row" spacing={1.1} alignItems="center">
                            <Icon sx={{ color: field.color }} />
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {field.label}
                              </Typography>
                              <Typography variant="h6" sx={{ color: field.color, fontWeight: 800 }}>
                                {Number(quota?.[field.key] || 0)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    )
                  })}
                </Grid>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2.5, height: '100%' }}>
                    <Stack spacing={1.75}>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <Payments color="primary" />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={800}>
                            Purchase via payment link
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Create a billable top-up request. Credits are added only after the payment link is paid.
                          </Typography>
                        </Box>
                      </Stack>

                      <Grid container spacing={1.25}>
                        {QUOTA_FIELDS.map((field) => (
                          <Grid item xs={6} sm={4} key={`purchase-${field.key}`}>
                            <TextField
                              fullWidth
                              label={field.label}
                              value={purchaseForm?.[field.key] ?? '0'}
                              onChange={updatePurchaseField(field.key)}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Amount"
                            value={purchaseForm?.requestedAmount ?? ''}
                            onChange={updatePurchaseField('requestedAmount')}
                            inputProps={{ min: 0, step: '0.01' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Currency"
                            value={purchaseForm?.currencyCode ?? 'INR'}
                            onChange={updatePurchaseField('currencyCode')}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Reference"
                            value={purchaseForm?.reference ?? ''}
                            onChange={updatePurchaseField('reference')}
                            placeholder="Invoice / finance / CRM reference"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Note"
                            value={purchaseForm?.note ?? ''}
                            onChange={updatePurchaseField('note')}
                            placeholder="What this top-up is for"
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {purchaseCredits > 0
                            ? `${purchaseCredits} total credits selected`
                            : 'Select quota items and amount to create the payment link'}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={handleCreatePurchaseRequest}
                          disabled={purchaseSubmitting || purchaseCredits <= 0}
                        >
                          {purchaseSubmitting ? 'Creating...' : 'Create payment link'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2.5, height: '100%' }}>
                    <Stack spacing={1.75}>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <Inventory2Outlined color="secondary" />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={800}>
                            Support allotment
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add quota directly as an explicit support-side allotment with an audit trail.
                          </Typography>
                        </Box>
                      </Stack>

                      <Grid container spacing={1.25}>
                        {QUOTA_FIELDS.map((field) => (
                          <Grid item xs={6} sm={4} key={`allot-${field.key}`}>
                            <TextField
                              fullWidth
                              label={field.label}
                              value={allotForm?.[field.key] ?? '0'}
                              onChange={updateAllotField(field.key)}
                              inputProps={{ inputMode: 'numeric', min: 0 }}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Reference"
                            value={allotForm?.reference ?? ''}
                            onChange={updateAllotField('reference')}
                            placeholder="Reason code / ticket / approval reference"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Note"
                            value={allotForm?.note ?? ''}
                            onChange={updateAllotField('note')}
                            placeholder="Why this support allotment is being made"
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {allotCredits > 0
                            ? `${allotCredits} total credits selected`
                            : 'Select quota items to record the allotment'}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleAllotQuota}
                          disabled={allotSubmitting || allotCredits <= 0}
                        >
                          {allotSubmitting ? 'Allotting...' : 'Allot quota'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2.5 }}>
                <Stack spacing={1.75}>
                  <Stack direction="row" spacing={1.1} alignItems="center">
                    <History color="action" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Recent quota activity
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Every purchase link and support allotment is tracked here.
                      </Typography>
                    </Box>
                  </Stack>

                  {requests.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No quota purchase or allotment records are available for this clinic brand yet.
                    </Alert>
                  ) : (
                    <Stack spacing={1.35}>
                      {requests.map((request, index) => (
                        <Paper
                          key={request?.id || `${request?.requestType || 'request'}-${index}`}
                          variant="outlined"
                          sx={{ p: 1.75, borderRadius: 2 }}
                        >
                          <Stack spacing={1}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle2" fontWeight={800}>
                                  {request?.requestType === 'purchase' ? 'Purchase request' : 'Support allotment'}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={request?.status || 'requested'}
                                  color={getStatusChipColor(request?.status)}
                                  variant="outlined"
                                />
                                {request?.requestedAmount > 0 ? (
                                  <Chip
                                    size="small"
                                    label={formatAmount(request?.requestedAmount, request?.currencyCode)}
                                    variant="outlined"
                                  />
                                ) : null}
                              </Stack>

                              <Typography variant="caption" color="text.secondary">
                                {formatDateTime(request?.requestedTime || request?.createdTime)}
                              </Typography>
                            </Stack>

                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {renderCreditChips(request)}
                            </Stack>

                            <Stack spacing={0.35}>
                              {request?.requestedByName ? (
                                <Typography variant="body2" color="text.secondary">
                                  Requested by {request.requestedByName}
                                </Typography>
                              ) : null}
                              {request?.reference ? (
                                <Typography variant="body2" color="text.secondary">
                                  Reference: {request.reference}
                                </Typography>
                              ) : null}
                              {request?.note ? (
                                <Typography variant="body2" color="text.secondary">
                                  {request.note}
                                </Typography>
                              ) : null}
                              {request?.failureReason ? (
                                <Typography variant="body2" color="error.main">
                                  {request.failureReason}
                                </Typography>
                              ) : null}
                            </Stack>

                            {request?.paymentLinkUrl ? (
                              <>
                                <Divider />
                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                                  <Typography variant="body2" color="text.secondary">
                                    Payment link is ready for the clinic brand billing contact.
                                  </Typography>
                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Copy payment link">
                                      <IconButton size="small" onClick={() => handleCopyPaymentLink(request?.paymentLinkUrl)}>
                                        <ContentCopy fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Open payment link">
                                      <IconButton
                                        size="small"
                                        onClick={() => window.open(request?.paymentLinkUrl, '_blank', 'noopener,noreferrer')}
                                      >
                                        <OpenInNew fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Stack>
                              </>
                            ) : null}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Paper>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={purchaseSubmitting || allotSubmitting}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
